import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import {
  Button,
  Card,
  Checkbox,
  Col,
  Drawer,
  message,
  Row,
  Typography,
} from 'antd';
import { yupResolver } from '@hookform/resolvers/yup';
import { Container, FormFieldHeading, MessageBox } from '@/components/Elements';
import { IncomeDetailFormType } from '../types';
import { useCountryOptions } from '@/hooks/useCountryOptions';

import {
  DatePickerField,
  InputField,
  ListOptionsField,
  RadioGroupField,
  SelectField,
  TextAreaField,
} from '@/components/Form';
import {
  AUS_PROPERTY_OPTIONS,
  INCOME_SOURCE_OPTIONS,
} from '../constant/optionList';

import useUnsavedChangesWarning from '@/hooks/useUnsavedChangesWarning';
import StepperFormLayout from '@/components/Layout/StepperFormLayout';
import {
  AGREE_OPTIONS,
  CITIZENSHIP_OPTIONS,
  FREQUENCY_OPTIONS,
  LOAN_TYPE_OPTIONS,
  MONTHS,
  RELATIONSHIP_OPTIONS,
  RESIDENTIAL_STATUS_OPTIONS,
  WHERE_DID_YOU_HEAR_ABOUT_US_OPTIONS,
  YEARS,
} from '@/constant/options';
import {
  incomeDetailLabelMapper,
  incomeDetailSchema,
} from '../schema/incomeDetailSchema';
import { getCurrencyByCountry, currencies } from '@/utils/currenciesUtils';
import { refinanceFormRoutes } from '../constant/refinanceStepperRoutes';
import useScrollToTop from '@/hooks/useScrollTop';
import { IncomeDetailPayload } from '@/pages/mortgage/types/Refinance';
import {
  useGetRefinanceRequestListQuery,
  useGetRefinanceStepDetailQuery,
  usePostRefinanceRequestMutation,
} from '@/pages/mortgage/apis/refinanceAPI';
import { displayError, displaySuccess } from '@/utils/displayMessageUtils';
import { useFetchUserProfileQuery } from '@/store/apis/userApi';
import StepProgress from '@/components/StepProgress/StepProgress';
import { stepData } from '../constant/stepData';
import { CompanyField } from '../components/Fields/CompanyField';
import { ContractorField } from '../components/Fields/ContractorField';
import { EmployeeFields } from '../components/Fields/EmployeeFields';
import {
  ADDITIONAL_TAX_BENEFITS,
  NON_PROPERTY_LOAN_OPTIONS,
} from '../../Purchase/constant/optionList';
import { CoapplicantContractorField } from '../components/Fields/CoapplicantContractorField';
import { CoapplicantCompanyField } from '../components/Fields/CoapplicantCompanyField';
import { CoapplicantEmployeeField } from '../components/Fields/CoapplicantEmployeeFields';
import dayjs from 'dayjs';
import MeetingModal from '../components/MeetingModal';
import FormMainLabel from '@/components/Elements/Heading/FormMainLabel';
import ReviewData from '../../Purchase/components/ReviewModal';
import { PrimaryDetailFormType } from '../../Purchase/types';
import makeAllFieldsOptional from '@/utils/yupUtils';
import { ObjectSchema } from 'yup';
import PhoneInputField from '@/components/Form/PhoneInputField';
import { useAppDispatch } from '@/hooks/hooks';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { setIncomeFormField } from '@/store/slices/refinance/refinanceSlice';
import { coapplicantKeys } from '../constant/incomeDetailKeys';
import { Car } from 'lucide-react';

const IncomeDetail = () => {
  const { id, update } = useParams();
  useUnsavedChangesWarning(true);
  useScrollToTop();
  const navigate = useNavigate();
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const total = 6;
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const termsRef = useRef<HTMLInputElement>(null);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const countryOptions = useCountryOptions();
  const [postRefinanceRequest, { isLoading: postRefinanceRequestLoading }] =
    usePostRefinanceRequestMutation();

  const [postRefinanceDraft, { isLoading: postRefinanceDraftLoading }] =
    usePostRefinanceRequestMutation();

  const { data: userProfileData } = useFetchUserProfileQuery();

  const { data: primaryDetailApiResponse } = useGetRefinanceStepDetailQuery(
    {
      application_id: id!,
      step: 'primary_detail',
    },
    {
      skip: !id,
      refetchOnMountOrArgChange: true,
    },
  );

  const primaryDetailData = primaryDetailApiResponse?.data
    ?.data as PrimaryDetailFormType;

  const {
    data: incomeDetailApiResponse,
    error: fetchIncomeDetailError,
    isLoading: fetchIncomeDetailLoading,
  } = useGetRefinanceStepDetailQuery(
    { application_id: id!, step: 'income_detail' },
    { skip: !id || !update, refetchOnMountOrArgChange: true },
  );
  const incomeDetailData = incomeDetailApiResponse?.data
    .data as IncomeDetailFormType;

  const incomeDetailSchemaWithOptionalFields =
    makeAllFieldsOptional(incomeDetailSchema);

  const dispatch = useAppDispatch();

  const { incomeDetail, dataPopulated } = useSelector(
    (state: RootState) => state.refinance,
  );

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    unregister,
    formState: { errors },
    resetField,
    reset,
  } = useForm<IncomeDetailFormType>({
    defaultValues: {
      salaryCurrency: 'AUD',
      coapplicantExists: undefined,
      creditCardCurrency: 'AUD',
    },
    resolver: yupResolver(
      incomeDetailSchemaWithOptionalFields as ObjectSchema<IncomeDetailFormType>,
    ),
  });

  useEffect(() => {
    if (primaryDetailData?.country) {
      const currency =
        getCurrencyByCountry(primaryDetailData.country)?.currency || '';
      reset({
        creditCardCurrency: currency,
      });
    }
  }, [
    primaryDetailData?.country,
    reset,
    primaryDetailData?.coapplicantCountry,
  ]);

  const [modalOpen, setModalOpen] = useState(false);

  const handleModalClose = () => {
    setModalOpen(false);
    navigate(`/mortgage/refinance`, { replace: true });
  };

  const handleModalOk = () => {
    navigate(`/mortgage/refinance`, { replace: true });
  };

  useEffect(() => {
    if (id && incomeDetailData) {
      setValue('incomeSource', incomeDetailData?.incomeSource);
      setValue('taxBenefitAwareness', incomeDetailData?.taxBenefitAwareness);
      if (incomeDetailData?.incomeSource === 'employee') {
        setValue('jobStartMonth', incomeDetailData?.jobStartMonth);
        setValue('jobStartYear', incomeDetailData?.jobStartYear);
        setValue('occupation', incomeDetailData?.occupation);
        setValue('employer', incomeDetailData?.employer);
        setValue('salaryCurrency', incomeDetailData?.salaryCurrency);
        setValue('baseSalary', incomeDetailData?.baseSalary);
        setValue('salaryFrequency', incomeDetailData?.salaryFrequency);
        setValue(
          'receiveAllowanceIncome',
          incomeDetailData?.receiveAllowanceIncome,
        );
        setValue('allowanceFrequency', incomeDetailData?.allowanceFrequency);
        setValue(
          'receiveCommissionIncome',
          incomeDetailData?.receiveCommissionIncome,
        );
        setValue(
          'receiveAnnualCashBonus',
          incomeDetailData?.receiveAnnualCashBonus,
        );
        setValue('receiveStockUnit', incomeDetailData?.receiveStockUnit);
      }
      setValue('nonPropertyLoan', incomeDetailData?.nonPropertyLoan);
      setValue('creditCardCurrency', incomeDetailData?.creditCardCurrency);
      setValue('creditCardLimit', incomeDetailData?.creditCardLimit);
      setValue('userMessage', incomeDetailData?.userMessage);

      if (incomeDetailData?.receiveAllowanceIncome) {
        setValue('allowanceIncome', incomeDetailData?.allowanceIncome);
        setValue(
          'typeOfAllowanceIncome',
          incomeDetailData?.typeOfAllowanceIncome,
        );
      }

      if (incomeDetailData?.receiveCommissionIncome) {
        setValue('commissionIncome', incomeDetailData?.commissionIncome);
        setValue('commissionFrequency', incomeDetailData?.commissionFrequency);
        setValue('commissionYears', incomeDetailData?.commissionYears);
      }

      if (incomeDetailData?.receiveAnnualCashBonus) {
        setValue(
          'thisYearAnnualCashBonus',
          incomeDetailData?.thisYearAnnualCashBonus,
        );
        setValue(
          'lastYearAnnualCashBonus',
          incomeDetailData?.lastYearAnnualCashBonus,
        );
      }

      if (incomeDetailData?.receiveStockUnit) {
        setValue(
          'thisYearStockOptions',
          incomeDetailData?.thisYearStockOptions,
        );
        setValue(
          'lastYearStockOptions',
          incomeDetailData?.lastYearStockOptions,
        );
      }

      if (incomeDetailData.nonPropertyLoan > 0) {
        for (let i = 1; i <= incomeDetailData.nonPropertyLoan; i++) {
          const propertyTypeField =
            `nonPropertyLoanType${i}` as keyof IncomeDetailFormType;
          const propertyCurrency =
            `nonPropertyLoanCurrency${i}` as keyof IncomeDetailFormType;
          const propertyBalance =
            `nonPropertyLoanBalance${i}` as keyof IncomeDetailFormType;
          const repaymentTerm =
            `nonPropertyLoanRepaymentTerm${i}` as keyof IncomeDetailFormType;
          const repaymentSum =
            `nonPropertyLoanRepaymentSum${i}` as keyof IncomeDetailFormType;
          const repaymentFrequency =
            `nonPropertyLoanRepaymentFrequency${i}` as keyof IncomeDetailFormType;

          setValue(propertyTypeField, incomeDetailData[propertyTypeField]);
          setValue(propertyCurrency, incomeDetailData[propertyCurrency]);
          setValue(propertyBalance, incomeDetailData[propertyBalance]);
          setValue(repaymentTerm, incomeDetailData[repaymentTerm]);
          setValue(repaymentSum, incomeDetailData[repaymentSum]);
          setValue(repaymentFrequency, incomeDetailData[repaymentFrequency]);
        }
      }

      if (
        incomeDetailData?.incomeSource ===
          'self_employed_trader_or_contractor' ||
        incomeDetailData?.incomeSource === 'self_employed_company'
      ) {
        setValue('operationStartMonth', incomeDetailData?.operationStartMonth);
        setValue('operationStartYear', incomeDetailData?.operationStartYear);
        setValue('industry', incomeDetailData?.industry);
        setValue(
          'companySalaryCurrency',
          incomeDetailData?.companySalaryCurrency,
        );
        setValue(
          'thisYearPersonalNetIncomeAfterTaxes',
          incomeDetailData?.thisYearPersonalNetIncomeAfterTaxes,
        );
        setValue(
          'lastYearPersonalNetIncomeAfterTaxes',
          incomeDetailData?.lastYearPersonalNetIncomeAfterTaxes,
        );
        setValue('ownershipPercentage', incomeDetailData?.ownershipPercentage);
        setValue(
          'thisYearCompanyNetIncomeAfterTaxes',
          incomeDetailData?.thisYearCompanyNetIncomeAfterTaxes,
        );
        setValue(
          'lastYearCompanyNetIncomeAfterTaxes',
          incomeDetailData?.lastYearCompanyNetIncomeAfterTaxes,
        );
      }
      setValue(
        'whereDidYouHearAboutUs',
        incomeDetailData?.whereDidYouHearAboutUs,
      );

      setValue('coapplicantExists', incomeDetailData?.coapplicantExists);

      if (incomeDetailData?.coapplicantExists) {
        setValue(
          'coapplicantFirstName',
          incomeDetailData?.coapplicantFirstName,
        );
        setValue('coapplicantLastName', incomeDetailData?.coapplicantLastName);
        setValue('coapplicantEmail', incomeDetailData?.coapplicantEmail);
        setValue('coapplicantPhone', incomeDetailData?.coapplicantPhone);
        setValue(
          'coapplicantCreditCardCurrency',
          incomeDetailData?.coapplicantCreditCardCurrency,
        );
        setValue(
          'coapplicantCreditCardLimit',
          incomeDetailData?.coapplicantCreditCardLimit,
        );
        setValue(
          'coapplicantRelationship',
          incomeDetailData?.coapplicantRelationship,
        );
        setValue(
          'coapplicantVisaSubClass',
          incomeDetailData?.coapplicantVisaSubClass,
        );
        setValue(
          'coapplicantVisaExpiryDate',
          incomeDetailData?.coapplicantVisaExpiryDate,
        );
        setValue(
          'coapplicantNonPropertyLoan',
          incomeDetailData?.coapplicantNonPropertyLoan,
        );
        setValue(
          'coapplicantCitizenship',
          incomeDetailData?.coapplicantCitizenship,
        );
        setValue(
          'coapplicantLivingTogether',
          incomeDetailData?.coapplicantLivingTogether,
        );
        setValue(
          'coapplicantIncomeSource',
          incomeDetailData?.coapplicantIncomeSource,
        );
        setValue('coapplicantCountry', incomeDetailData?.coapplicantCountry);
        setValue(
          'coapplicantPermanentResident',
          incomeDetailData?.coapplicantPermanentResident,
        );
        setValue(
          'coapplicantCurrentResidentalStatus',
          incomeDetailData?.coapplicantCurrentResidentalStatus,
        );
        setValue(
          'coapplicantRentExpense',
          incomeDetailData?.coapplicantRentExpense,
        );
        setValue(
          'coapplicantFrequencyOfRent',
          incomeDetailData?.coapplicantFrequencyOfRent,
        );
        setValue(
          'coapplicantIsTheResidentialPlaceMortgaged',
          incomeDetailData?.coapplicantIsTheResidentialPlaceMortgaged,
        );
        setValue(
          'coapplicantResidentalOtherSpecification',
          incomeDetailData?.coapplicantResidentalOtherSpecification,
        );
        if (incomeDetailData?.coapplicantIncomeSource === 'employee') {
          setValue(
            'coapplicantJobStartMonth',
            incomeDetailData?.coapplicantJobStartMonth,
          );
          setValue(
            'coapplicantJobStartYear',
            incomeDetailData?.coapplicantJobStartYear,
          );
          setValue(
            'coapplicantOccupation',
            incomeDetailData?.coapplicantOccupation,
          );
          setValue(
            'coapplicantEmployer',
            incomeDetailData?.coapplicantEmployer,
          );
          setValue(
            'coapplicantSalaryCurrency',
            incomeDetailData?.coapplicantSalaryCurrency,
          );
          setValue(
            'coapplicantBaseSalary',
            incomeDetailData?.coapplicantBaseSalary,
          );
          setValue(
            'coapplicantReceiveAllowanceIncome',
            incomeDetailData?.coapplicantReceiveAllowanceIncome,
          );
          setValue(
            'coapplicantAllowanceIncome',
            incomeDetailData?.coapplicantAllowanceIncome,
          );
          setValue(
            'coapplicantTypeOfAllowanceIncome',
            incomeDetailData?.coapplicantTypeOfAllowanceIncome,
          );
          setValue(
            'coapplicantReceiveCommissionIncome',
            incomeDetailData?.coapplicantReceiveCommissionIncome,
          );
          setValue(
            'coapplicantCommissionIncome',
            incomeDetailData?.coapplicantCommissionIncome,
          );
          setValue(
            'coapplicantReceiveAnnualCashBonus',
            incomeDetailData?.coapplicantReceiveAnnualCashBonus,
          );
          setValue(
            'coapplicantSalaryFrequency',
            incomeDetailData?.coapplicantSalaryFrequency,
          );

          if (incomeDetailData?.coapplicantReceiveAllowanceIncome) {
            setValue(
              'coapplicantAllowanceFrequency',
              incomeDetailData?.coapplicantAllowanceFrequency,
            );
          }

          if (incomeDetailData?.coapplicantReceiveAnnualCashBonus) {
            setValue(
              'coapplicantThisYearAnnualCashBonus',
              incomeDetailData?.coapplicantThisYearAnnualCashBonus,
            );
            setValue(
              'coapplicantLastYearAnnualCashBonus',
              incomeDetailData?.coapplicantLastYearAnnualCashBonus,
            );
          }

          if (incomeDetailData?.coapplicantReceiveCommissionIncome) {
            setValue(
              'coapplicantCommissionFrequency',
              incomeDetailData?.coapplicantCommissionFrequency,
            );
          }

          setValue(
            'coapplicantReceiveStockUnit',
            incomeDetailData?.coapplicantReceiveStockUnit,
          );
          if (incomeDetailData?.coapplicantReceiveStockUnit) {
            setValue(
              'coapplicantThisYearStockOptions',
              incomeDetailData?.coapplicantThisYearStockOptions,
            );
            setValue(
              'coapplicantLastYearStockOptions',
              incomeDetailData?.coapplicantLastYearStockOptions,
            );
          }
        }

        if (
          incomeDetailData?.coapplicantIncomeSource ===
          'self_employed_trader_or_contractor'
        ) {
          setValue(
            'coapplicantOperationStartMonth',
            incomeDetailData?.coapplicantOperationStartMonth,
          );
          setValue(
            'coapplicantOperationStartYear',
            incomeDetailData?.coapplicantOperationStartYear,
          );
          setValue(
            'coapplicantIndustry',
            incomeDetailData?.coapplicantIndustry,
          );
          setValue(
            'coapplicantCompanySalaryCurrency',
            incomeDetailData?.coapplicantCompanySalaryCurrency,
          );
          setValue(
            'coapplicantThisYearPersonalNetIncomeAfterTaxes',
            incomeDetailData?.coapplicantThisYearPersonalNetIncomeAfterTaxes,
          );
          setValue(
            'coapplicantLastYearPersonalNetIncomeAfterTaxes',
            incomeDetailData?.coapplicantLastYearPersonalNetIncomeAfterTaxes,
          );
        }

        if (
          incomeDetailData?.coapplicantIncomeSource === 'self_employed_company'
        ) {
          setValue(
            'coapplicantOperationStartMonth',
            incomeDetailData?.coapplicantOperationStartMonth,
          );
          setValue(
            'coapplicantOperationStartYear',
            incomeDetailData?.coapplicantOperationStartYear,
          );
          setValue(
            'coapplicantIndustry',
            incomeDetailData?.coapplicantIndustry,
          );
          setValue(
            'coapplicantCompanySalaryCurrency',
            incomeDetailData?.coapplicantCompanySalaryCurrency,
          );
          setValue(
            'coapplicantThisYearPersonalNetIncomeAfterTaxes',
            incomeDetailData?.coapplicantThisYearPersonalNetIncomeAfterTaxes,
          );
          setValue(
            'coapplicantLastYearPersonalNetIncomeAfterTaxes',
            incomeDetailData?.coapplicantLastYearPersonalNetIncomeAfterTaxes,
          );
          setValue(
            'coapplicantOwnershipPercentage',
            incomeDetailData?.coapplicantOwnershipPercentage,
          );
          setValue(
            'coapplicantThisYearCompanyNetIncomeAfterTaxes',
            incomeDetailData?.coapplicantThisYearCompanyNetIncomeAfterTaxes,
          );
          setValue(
            'coapplicantLastYearCompanyNetIncomeAfterTaxes',
            incomeDetailData?.coapplicantLastYearCompanyNetIncomeAfterTaxes,
          );
        }

        if (
          incomeDetailData?.coapplicantNonPropertyLoan &&
          incomeDetailData.coapplicantNonPropertyLoan > 0
        ) {
          for (
            let i = 1;
            i <= incomeDetailData.coapplicantNonPropertyLoan;
            i++
          ) {
            const propertyTypeField =
              `coapplicantNonPropertyLoanType${i}` as keyof IncomeDetailFormType;
            const propertyCurrency =
              `coapplicantNonPropertyLoanCurrency${i}` as keyof IncomeDetailFormType;
            const propertyBalance =
              `coapplicantNonPropertyLoanBalance${i}` as keyof IncomeDetailFormType;
            const repaymentTerm =
              `coapplicantNonPropertyLoanRepaymentTerm${i}` as keyof IncomeDetailFormType;
            const repaymentSum =
              `coapplicantNonPropertyLoanRepaymentSum${i}` as keyof IncomeDetailFormType;
            const repaymentFrequency =
              `coapplicantNonPropertyLoanRepaymentFrequency${i}` as keyof IncomeDetailFormType;

            setValue(propertyTypeField, incomeDetailData[propertyTypeField]);
            setValue(propertyCurrency, incomeDetailData[propertyCurrency]);
            setValue(propertyBalance, incomeDetailData[propertyBalance]);
            setValue(repaymentTerm, incomeDetailData[repaymentTerm]);
            setValue(repaymentSum, incomeDetailData[repaymentSum]);
            setValue(repaymentFrequency, incomeDetailData[repaymentFrequency]);
          }
        }
      }
    } else if (id && userProfileData) {
      const userDetail = userProfileData?.data?.common_data;
      setValue('incomeSource', userDetail.incomeSource ?? '');
      if (userDetail?.incomeSource === 'employee') {
        setValue('jobStartMonth', userDetail?.jobStartMonth);
        setValue('jobStartYear', userDetail?.jobStartYear);
        setValue('occupation', userDetail?.occupation);
        setValue('employer', userDetail?.employer);
        setValue('salaryCurrency', userDetail?.salaryCurrency);
        setValue('baseSalary', userDetail?.baseSalary);
        setValue('salaryFrequency', userDetail?.salaryFrequency);
      }
    }
  }, [incomeDetailData, userProfileData]);

  const watchIncomeSource = watch('incomeSource');
  const watchCoapplicantCountry = watch('coapplicantCountry');
  const watchSalaryCurrency = watch('salaryCurrency');
  const watchCompanySalaryCurrency = watch('companySalaryCurrency');
  const watchNonProperties = watch('nonPropertyLoan') || 0;
  const watchCreditCardCurrency = watch('creditCardCurrency');
  const watchAllowanceIncome = watch('receiveAllowanceIncome');
  const watchCommissionIncome = watch('receiveCommissionIncome');
  const watchAnnualCashBonus = watch('receiveAnnualCashBonus');
  const watchStockUnit = watch('receiveStockUnit');

  const watchcoapplicantExists = watch('coapplicantExists');

  const watchCoapplicantIncomeSource = watch('coapplicantIncomeSource');
  const watchCoapplicantAllowanceIncome = watch(
    'coapplicantReceiveAllowanceIncome',
  );
  const watchCoapplicantCommissionIncome = watch(
    'coapplicantReceiveCommissionIncome',
  );
  const watchCoapplicantAnnualCashBonus = watch(
    'coapplicantReceiveAnnualCashBonus',
  );
  const watchCoapplicantStockUnit = watch('coapplicantReceiveStockUnit');
  const watchCoapplicationNonProperty =
    watch('coapplicantNonPropertyLoan') || 0;
  const watchCoapplicantSalaryCurrency = watch('coapplicantSalaryCurrency');
  const watchCoapplicantCompanySalaryCurrency = watch(
    'coapplicantCompanySalaryCurrency',
  );
  const watchCoapplicantCitizenship = watch('coapplicantCitizenship');
  const watchCoapplicantCreditCardCurrency = watch(
    'coapplicantCreditCardCurrency',
  );

  useEffect(() => {
    if (watchCoapplicationNonProperty) {
      const currentNonProperty = watchCoapplicationNonProperty || 0;
      const previousNonPropertyValue = Object.keys(getValues()).filter((key) =>
        key.startsWith('coapplicantNonPropertyLoan'),
      ).length;

      if (currentNonProperty < previousNonPropertyValue) {
        for (
          let i = currentNonProperty + 1;
          i <= previousNonPropertyValue;
          i++
        ) {
          clearCoapplicantNonPropertyLoanFields(i);
        }
      }
    }
  }, [watchCoapplicationNonProperty, unregister]);

  useEffect(() => {
    if (watchNonProperties) {
      const currentNonProperty = watchNonProperties || 0;
      const previousNonPropertyValue = Object.keys(getValues()).filter((key) =>
        key.startsWith('nonPropertyLoan'),
      ).length;

      if (currentNonProperty < previousNonPropertyValue) {
        for (
          let i = currentNonProperty + 1;
          i <= previousNonPropertyValue;
          i++
        ) {
          clearNonPropertyLoanFields(i);
        }
      }
    }
  }, [watchNonProperties, unregister]);

  useEffect(() => {
    if (watchIncomeSource) {
      if (watchIncomeSource !== 'employee') {
        unregister('jobStartMonth');
        unregister('jobStartYear');
        unregister('occupation');
        unregister('employer');
        unregister('salaryCurrency');
        unregister('baseSalary');
        unregister('salaryFrequency');
        unregister('receiveAllowanceIncome');
        unregister('allowanceIncome');
        unregister('allowanceFrequency');
        unregister('typeOfAllowanceIncome');
        unregister('receiveCommissionIncome');
        unregister('commissionIncome');
        unregister('commissionFrequency');
        unregister('commissionYears');
        unregister('receiveAnnualCashBonus');
        unregister('thisYearAnnualCashBonus');
        unregister('lastYearAnnualCashBonus');
        unregister('receiveStockUnit');
        unregister('thisYearStockOptions');
        unregister('lastYearStockOptions');
      }

      if (
        watchIncomeSource !== 'self_employed_trader_or_contractor' &&
        watchIncomeSource !== 'self_employed_company'
      ) {
        unregister('operationStartMonth');
        unregister('operationStartYear');
        unregister('industry');
        unregister('companySalaryCurrency');
        unregister('thisYearPersonalNetIncomeAfterTaxes');
        unregister('lastYearPersonalNetIncomeAfterTaxes');
        unregister('ownershipPercentage');
        unregister('thisYearCompanyNetIncomeAfterTaxes');
        unregister('lastYearCompanyNetIncomeAfterTaxes');
      }
    }
  }, [watchIncomeSource, unregister]);

  useEffect(() => {
    if (watchCoapplicantIncomeSource) {
      if (watchCoapplicantIncomeSource !== 'employee') {
        unregister('coapplicantJobStartMonth');
        unregister('coapplicantJobStartYear');
        unregister('coapplicantOccupation');
        unregister('coapplicantEmployer');
        unregister('coapplicantSalaryCurrency');
        unregister('coapplicantBaseSalary');
        unregister('coapplicantSalaryFrequency');
        unregister('coapplicantReceiveAllowanceIncome');
        unregister('coapplicantAllowanceIncome');
        unregister('coapplicantAllowanceFrequency');
        unregister('coapplicantTypeOfAllowanceIncome');
        unregister('coapplicantReceiveCommissionIncome');
        unregister('coapplicantCommissionIncome');
        unregister('coapplicantCommissionFrequency');
        unregister('coapplicantCommissionYears');
        unregister('coapplicantReceiveAnnualCashBonus');
        unregister('coapplicantThisYearAnnualCashBonus');
        unregister('coapplicantLastYearAnnualCashBonus');
        unregister('coapplicantReceiveStockUnit');
        unregister('coapplicantThisYearStockOptions');
        unregister('coapplicantLastYearStockOptions');
      }

      if (
        watchCoapplicantIncomeSource !== 'self_employed_trader_or_contractor' &&
        watchCoapplicantIncomeSource !== 'self_employed_company'
      ) {
        unregister('coapplicantOperationStartMonth');
        unregister('coapplicantOperationStartYear');
        unregister('coapplicantIndustry');
        unregister('coapplicantCompanySalaryCurrency');
        unregister('coapplicantThisYearPersonalNetIncomeAfterTaxes');
        unregister('coapplicantLastYearPersonalNetIncomeAfterTaxes');
        unregister('coapplicantOwnershipPercentage');
        unregister('coapplicantThisYearCompanyNetIncomeAfterTaxes');
        unregister('lastYearCompanyNetIncomeAfterTaxes');
      }

      if (watchCoapplicantIncomeSource !== 'self_employed_company') {
        unregister('coapplicantOwnershipPercentage');
        unregister('coapplicantThisYearCompanyNetIncomeAfterTaxes');
        unregister('coapplicantLastYearCompanyNetIncomeAfterTaxes');
      }
    }
  }, [watchCoapplicantIncomeSource, unregister]);

  const saveAsDraft = async () => {
    const rawData = getValues();
    const data = incomeDetailSchemaWithOptionalFields.cast(rawData, {
      stripUnknown: true,
    });
    const payload = {
      data,
      draft: true,
      step: 'income_detail' as const,
      ...(id ? { application_id: id } : {}),
    };
    try {
      const res = await postRefinanceDraft(payload);
      if (res?.data?.success) {
        message.success(res?.data?.message || 'Draft saved successfully');
      } else {
        message.error(
          res?.data?.error
            ? JSON.stringify(res.data.error)
            : 'Failed to save draft',
        );
      }
    } catch (error: any) {
      // Handle network/axios errors separately
      const errMsg =
        error?.response?.data?.error ||
        error?.message ||
        'Error while saving as draft';
      message.error(
        typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg),
      );
    }
  };

  const onSubmit = (data: IncomeDetailFormType) => {
    if (!acceptedTerms) {
      if (termsRef.current) {
        termsRef.current.scrollIntoView({ behavior: 'smooth' });
      }
      message.error('Please accept the terms and conditions.');
      return;
    }
    setDrawerOpen(true);
  };

  const handleFormSubmit = () => {
    if (submitButtonRef.current) {
      submitButtonRef.current.click();
    }
  };

  const handleBackClick = () => {
    Object.keys(getValues()).forEach((key) => {
      dispatch(
        setIncomeFormField({
          field: key as keyof IncomeDetailFormType,
          value: getValues()[key as keyof IncomeDetailFormType] ?? undefined,
        }),
      );
    });
    const route = `${refinanceFormRoutes['primary_detail']}/${id}`;
    navigate(route);
  };

  const clearNonPropertyLoanFields = (index: number) => {
    const fields = [
      `nonPropertyLoanType${index}`,
      `nonPropertyLoanCurrency${index}`,
      `nonPropertyLoanBalance${index}`,
      `nonPropertyLoanRepaymentTerm${index}`,
      `nonPropertyLoanRepaymentSum${index}`,
      `nonPropertyLoanRepaymentFrequency${index}`,
    ];

    fields.forEach((field) => {
      unregister(field as keyof IncomeDetailFormType);
    });
  };

  const clearCoapplicantNonPropertyLoanFields = (index: number) => {
    const fields = [
      `coapplicantNonPropertyLoanType${index}`,
      `coapplicantNonPropertyLoanCurrency${index}`,
      `coapplicantNonPropertyLoanBalance${index}`,
      `coapplicantNonPropertyLoanRepaymentTerm${index}`,
      `coapplicantNonPropertyLoanRepaymentSum${index}`,
      `coapplicantNonPropertyLoanRepaymentFrequency${index}`,
    ];

    fields.forEach((field) => {
      unregister(field as keyof IncomeDetailFormType);
    });
  };

  useEffect(() => {
    if (watchCoapplicationNonProperty) {
      const currentNonProperty = watchCoapplicationNonProperty || 0;
      const previousNonPropertyValue = Object.keys(getValues()).filter((key) =>
        key.startsWith('coapplicantNonPropertyLoan'),
      ).length;

      if (currentNonProperty < previousNonPropertyValue) {
        for (
          let i = currentNonProperty + 1;
          i <= previousNonPropertyValue;
          i++
        ) {
          clearCoapplicantNonPropertyLoanFields(i);
        }
      }
    }
  }, [watchCoapplicationNonProperty, unregister]);

  const [numberIndex, setNumberIndex] = useState(0);
  const [coapplicantNumberIndex, setCoapplicantNumberIndex] = useState(0);

  useEffect(() => {
    if (watchIncomeSource === 'self_employed_trader_or_contractor') {
      setNumberIndex(5);
    } else if (watchIncomeSource === 'self_employed_company') {
      setNumberIndex(7);
    } else if (watchIncomeSource === 'employee') {
      setNumberIndex(9);
    } else {
      setNumberIndex(2);
    }
  }, [watchIncomeSource]);

  useEffect(() => {
    if (watchCoapplicantIncomeSource === 'self_employed_trader_or_contractor') {
      setCoapplicantNumberIndex(6);
    } else if (watchCoapplicantIncomeSource === 'self_employed_company') {
      setCoapplicantNumberIndex(6);
    } else if (watchCoapplicantIncomeSource === 'employee') {
      setCoapplicantNumberIndex(10);
    } else {
      setCoapplicantNumberIndex(3);
    }
  }, [watchCoapplicantIncomeSource]);

  useEffect(() => {
    if (dataPopulated && incomeDetail) {
      Object.keys(incomeDetail).forEach((key) => {
        if (incomeDetail[key as keyof IncomeDetailFormType] !== undefined) {
          setValue(
            key as keyof IncomeDetailFormType,
            incomeDetail[key as keyof IncomeDetailFormType] ?? undefined,
          );
        }
      });
      console.log('incomeDetail', incomeDetail, 'values', getValues());
    }
  }, [dataPopulated, incomeDetail]);

  useEffect(() => {
    if (watchcoapplicantExists === false && !dataPopulated) {
      coapplicantKeys.forEach((key) => {
        setValue(key as keyof IncomeDetailFormType, undefined);
      });
    }
  }, [watchcoapplicantExists]);

  useEffect(() => {
    if (watchcoapplicantExists === false && dataPopulated) {
      dispatch(
        setIncomeFormField({
          field: 'coapplicantExists',
          value: false,
        }),
      );
      coapplicantKeys.forEach((key) => {
        dispatch(
          setIncomeFormField({
            field: key as keyof IncomeDetailFormType,
            value: null,
          }),
        );
      });
    }
  }, [watchcoapplicantExists, dataPopulated]);

  // useEffect(() => {
  //   if (watchcoapplicantExists === true) {
  //     coapplicantKeys.forEach((key) => {
  //       if (key !== 'coapplicantExists') {
  //         setValue(key as keyof IncomeDetailFormType, undefined);
  //       }
  //     });
  //   }
  // }, [watchcoapplicantExists, setValue]);

  useEffect(() => {
    if (watchcoapplicantExists === false) {
      coapplicantKeys.forEach((key) => {
        resetField(key as keyof IncomeDetailFormType);
      });
    }
  }, [watchcoapplicantExists, resetField]);

  const resetNonPropertyLoanFields = () => {
    const nonPropertyLoanFields = Object.keys(getValues()).filter(
      (key) =>
        key.startsWith('nonPropertyLoan') &&
        parseInt(key.slice(-1)) > watchNonProperties,
    );
    nonPropertyLoanFields.forEach((field) => {
      resetField(field as keyof IncomeDetailFormType);
    });
  };

  const resetCoapplicantNonPropertyLoanFields = () => {
    const coapplicantNonPropertyLoanFields = Object.keys(getValues()).filter(
      (key) =>
        key.startsWith('coapplicantNonPropertyLoan') &&
        parseInt(key.slice(-1)) > watchCoapplicationNonProperty,
    );
    coapplicantNonPropertyLoanFields.forEach((field) => {
      resetField(field as keyof IncomeDetailFormType);
    });
  };

  useEffect(() => {
    resetCoapplicantNonPropertyLoanFields();
    resetNonPropertyLoanFields();
  }, [watchCoapplicationNonProperty, watchNonProperties]);

  const watchCoapplicantLivingTogether = watch('coapplicantLivingTogether');
  const watchCoapplicantCurrentResidentalStatus = watch(
    'coapplicantCurrentResidentalStatus',
  );

  useEffect(() => {
    if (watchCoapplicantLivingTogether === true) {
      resetField('coapplicantCountry');
      resetField('coapplicantPermanentResident');
      resetField('coapplicantCurrentResidentalStatus');
      resetField('coapplicantRentExpense');
      resetField('coapplicantFrequencyOfRent');
      resetField('coapplicantIsTheResidentialPlaceMortgaged');
      resetField('coapplicantResidentalOtherSpecification');
    }
  }, [watchCoapplicantLivingTogether, resetField]);

  useEffect(() => {
    if (watchCoapplicantCurrentResidentalStatus === 'renting') {
      resetField('coapplicantResidentalOtherSpecification');
      resetField('coapplicantIsTheResidentialPlaceMortgaged');
    }
    if (watchCoapplicantCurrentResidentalStatus === 'live_own_place') {
      resetField('coapplicantRentExpense');
      resetField('coapplicantFrequencyOfRent');
      resetField('coapplicantResidentalOtherSpecification');
    }
    if (
      watchCoapplicantCurrentResidentalStatus ===
        'rent_free_or_boarding_parent' ||
      watchCoapplicantCurrentResidentalStatus ===
        'rent_free_or_boarding_spouse' ||
      watchCoapplicantCurrentResidentalStatus ===
        'rent_free_or_boarding_friend' ||
      watchCoapplicantCurrentResidentalStatus ===
        'rent_free_or_boarding_employer'
    ) {
      resetField('coapplicantRentExpense');
      resetField('coapplicantFrequencyOfRent');
      resetField('coapplicantResidentalOtherSpecification');
      resetField('coapplicantIsTheResidentialPlaceMortgaged');
    }
    if (watchCoapplicantCurrentResidentalStatus === 'other') {
      resetField('coapplicantRentExpense');
      resetField('coapplicantFrequencyOfRent');
      resetField('coapplicantIsTheResidentialPlaceMortgaged');
    }
  }, [watchCoapplicantCurrentResidentalStatus, resetField]);

  useEffect(() => {
    if (
      watchCoapplicantCitizenship === 'foreign_national' ||
      watchCoapplicantCitizenship === 'newzeland_citizen' ||
      watchCoapplicantCitizenship === 'australian_citizen'
    ) {
      resetField('coapplicantVisaSubClass');
      resetField('coapplicantVisaExpiryDate');
    }
  }, [watchCoapplicantCitizenship, resetField]);

  return (
    <StepperFormLayout
      showBackButton={true}
      handleSubmit={handleFormSubmit}
      handleBackClick={handleBackClick}
      nextBtnText="Review Submission"
      isSubmitLoading={postRefinanceRequestLoading}
      showDraftButton={true}
      handleSaveAsDraftClick={saveAsDraft}
      isDraftLoading={postRefinanceDraftLoading}
    >
      <Container width="md">
        <Row
          style={{
            paddingBottom: '20px',
          }}
          gutter={[0, 4]}
        >
          <Col xs={24}>
            <StepProgress
              formId="personal_detail"
              id={id || ''}
              completedStep={1}
              totalSteps={total}
              stepData={stepData}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={24}>
            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              <Row gutter={0}>
                <Card
                  title={
                    <Typography.Title level={4} style={{ margin: 0 }}>
                      Applicant Income Details
                    </Typography.Title>
                  }
                  style={{ width: '100%', marginBottom: '20px' }}
                >
                  <Col xs={24}>
                    <div className="form-control">
                      <FormMainLabel
                        label={incomeDetailLabelMapper['incomeSource']}
                        index={1}
                      />
                      <RadioGroupField
                        name="incomeSource"
                        options={INCOME_SOURCE_OPTIONS}
                        error={errors.incomeSource?.message ?? ''}
                        control={control}
                        size="large"
                      />
                    </div>
                  </Col>
                  {watchIncomeSource === 'employee' && (
                    <EmployeeFields
                      control={control}
                      errors={errors}
                      watchSalaryCurrency={watchSalaryCurrency}
                      watchAllowanceIncome={watchAllowanceIncome}
                      watchCommissionIncome={watchCommissionIncome}
                      watchAnnualCashBonus={watchAnnualCashBonus}
                      watchStockUnit={watchStockUnit}
                      resetField={resetField}
                    />
                  )}
                  {watchIncomeSource ===
                    'self_employed_trader_or_contractor' && (
                    <ContractorField
                      control={control}
                      errors={errors}
                      watchSalaryCurrency={watchCompanySalaryCurrency}
                      watchAllowanceIncome={watchAllowanceIncome}
                      watchCommissionIncome={watchCommissionIncome}
                      watchAnnualCashBonus={watchAnnualCashBonus}
                      watchStockUnit={watchStockUnit}
                    />
                  )}
                  {watchIncomeSource === 'self_employed_company' && (
                    <CompanyField
                      control={control}
                      errors={errors}
                      watchSalaryCurrency={watchCompanySalaryCurrency}
                      watchAllowanceIncome={watchAllowanceIncome}
                      watchCommissionIncome={watchCommissionIncome}
                      watchAnnualCashBonus={watchAnnualCashBonus}
                      watchStockUnit={watchStockUnit}
                    />
                  )}
                  <Col xs={24}>
                    <div className="form-section-header form-control">
                      <FormMainLabel
                        label={incomeDetailLabelMapper['nonPropertyLoan']}
                        index={`${numberIndex}`}
                      />

                      <RadioGroupField
                        size="large"
                        name="nonPropertyLoan"
                        options={AUS_PROPERTY_OPTIONS}
                        error={errors.nonPropertyLoan?.message ?? ''}
                        control={control}
                        cols={{
                          sm: 2,
                          md: 4,
                          lg: 6,
                        }}
                      />
                    </div>
                  </Col>
                  {Array.from({ length: watchNonProperties }, (_, index) => {
                    const fieldIndex = index + 1;
                    return (
                      <Col xs={24} key={fieldIndex}>
                        <div className="form-section-header form-control">
                          <FormMainLabel
                            label={`Non-property Loan ${fieldIndex} Details`}
                          />
                          <RadioGroupField
                            size="large"
                            name={`nonPropertyLoanType${fieldIndex}`}
                            options={LOAN_TYPE_OPTIONS}
                            error={
                              (errors as any)[
                                `nonPropertyLoanType${fieldIndex}`
                              ]?.message ?? ''
                            }
                            control={control}
                          />
                        </div>
                        <Row gutter={30}>
                          <Col xs={24} md={12}>
                            <SelectField
                              showSearch
                              options={currencies}
                              name={`nonPropertyLoanCurrency${fieldIndex}`}
                              control={control}
                              size="large"
                              label="Currency"
                              placeholder="Select currency type"
                              error={
                                (errors as any)[
                                  `nonPropertyLoanCurrency${fieldIndex}`
                                ]?.message ?? ''
                              }
                            />
                          </Col>
                          <Col xs={24} md={12}>
                            <InputField
                              label="Loan Balance"
                              name={`nonPropertyLoanBalance${fieldIndex}`}
                              control={control}
                              error={
                                (errors as any)[
                                  `nonPropertyLoanBalance${fieldIndex}`
                                ]?.message ?? ''
                              }
                              placeholder="Enter your loan balance"
                              size="large"
                              type="number"
                            />
                          </Col>
                          <Col xs={24} md={12}>
                            <InputField
                              label="Repayment loan term"
                              name={`nonPropertyLoanRepaymentTerm${fieldIndex}`}
                              control={control}
                              error={
                                (errors as any)[
                                  `nonPropertyLoanRepaymentTerm${fieldIndex}`
                                ]?.message ?? ''
                              }
                              placeholder="Years left on loan term"
                              size="large"
                              type="number"
                              suffix="Years"
                            />
                          </Col>
                          <Col xs={24} md={12}>
                            <InputField
                              label="Repayment"
                              name={`nonPropertyLoanRepaymentSum${fieldIndex}`}
                              control={control}
                              error={
                                (errors as any)[
                                  `nonPropertyLoanRepaymentSum${fieldIndex}`
                                ]?.message ?? ''
                              }
                              placeholder="Your repayment Sum"
                              size="large"
                              type="number"
                            />
                          </Col>
                          <Col xs={24}>
                            <RadioGroupField
                              name={`nonPropertyLoanRepaymentFrequency${fieldIndex}`}
                              options={FREQUENCY_OPTIONS}
                              label="Repayment Frequency"
                              error={
                                (errors as any)[
                                  `nonPropertyLoanRepaymentFrequency${fieldIndex}`
                                ]?.message ?? ''
                              }
                              control={control}
                              size="large"
                            />
                          </Col>
                        </Row>
                      </Col>
                    );
                  })}

                  <Col xs={24}>
                    <div className="form-section-header form-control">
                      <FormMainLabel
                        index={`${numberIndex + 1}`}
                        label={incomeDetailLabelMapper['creditCard']}
                      />
                    </div>
                    <Row gutter={30}>
                      <Col xs={24} md={12}>
                        <SelectField
                          options={currencies}
                          name="creditCardCurrency"
                          control={control}
                          size="large"
                          showSearch={true}
                          label="Credit Card Currency"
                          placeholder="Select currency type"
                          error={errors?.creditCardCurrency?.message ?? ''}
                        />
                      </Col>
                      <Col xs={24} md={12}>
                        <InputField
                          label="Credit Card Limit Amount"
                          name="creditCardLimit"
                          control={control}
                          error={errors?.creditCardLimit?.message ?? ''}
                          placeholder="Enter your credit card limit"
                          size="large"
                          type="number"
                          prefix={watchCreditCardCurrency ?? ''}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Card>
                <Card
                  title={
                    <Typography.Title level={4} style={{ margin: 0 }}>
                      Co-applicant Income Details
                    </Typography.Title>
                  }
                  style={{ width: '100%', marginBottom: '20px' }}
                >
                  <Col xs={24}>
                    <div className="form-control">
                      <FormMainLabel
                        index={1}
                        label="Are you refinancing this property with someone else?"
                      />
                    </div>
                    <Row gutter={16}>
                      <Col xs={24}>
                        <RadioGroupField
                          size="large"
                          name="coapplicantExists"
                          options={AGREE_OPTIONS}
                          control={control}
                          error={errors?.coapplicantExists?.message ?? ''}
                        />
                      </Col>
                    </Row>
                    {watchcoapplicantExists && (
                      <Row gutter={30}>
                        <Col xs={24}>
                          <FormMainLabel
                            index={2}
                            label="Enter the co-applicant details?"
                          />
                        </Col>
                        <Col xs={24} md={12}>
                          <InputField
                            label="Coapplicant First Name"
                            name="coapplicantFirstName"
                            control={control}
                            error={errors?.coapplicantFirstName?.message ?? ''}
                            placeholder="Enter your first name"
                            size="large"
                          />
                        </Col>
                        <Col xs={24} md={12}>
                          <InputField
                            label="Coapplicant Last Name"
                            name="coapplicantLastName"
                            control={control}
                            error={errors?.coapplicantLastName?.message ?? ''}
                            placeholder="Enter your last name"
                            size="large"
                          />
                        </Col>
                        <Col xs={24} md={12}>
                          <InputField
                            label="Coapplicant Email"
                            name="coapplicantEmail"
                            control={control}
                            error={errors?.coapplicantEmail?.message ?? ''}
                            placeholder="Enter your email"
                            size="large"
                          />
                        </Col>
                        <Col xs={24} md={12}>
                          <PhoneInputField
                            label="Coapplicant Phone"
                            name="coapplicantPhone"
                            control={control}
                            error={errors?.coapplicantPhone?.message ?? ''}
                          />
                        </Col>
                        <Col xs={24}>
                          <RadioGroupField
                            name="coapplicantRelationship"
                            options={RELATIONSHIP_OPTIONS}
                            control={control}
                            label="Coapplicant Relationship"
                            size="large"
                            error={
                              errors?.coapplicantRelationship?.message ?? ''
                            }
                          />
                        </Col>
                        <Col xs={24}>
                          <SelectField
                            options={CITIZENSHIP_OPTIONS}
                            placeholder="Select citizenship"
                            name="coapplicantCitizenship"
                            showSearch={true}
                            control={control}
                            label="Coapplicant Citizenship"
                            error={
                              errors?.coapplicantCitizenship?.message ?? ''
                            }
                            size="large"
                          />
                        </Col>
                        {(watchCoapplicantCitizenship ===
                          'australian_pr_visa' ||
                          watchCoapplicantCitizenship ===
                            'australian_tr_visa') && (
                          <>
                            <Col xs={24} md={12}>
                              <InputField
                                label="Visa Sub-Class"
                                name="coapplicantVisaSubClass"
                                control={control}
                                error={
                                  errors.coapplicantVisaSubClass?.message ?? ''
                                }
                                placeholder="Enter your visa subclass"
                                size="large"
                              />
                            </Col>
                            <Col xs={24} md={12}>
                              <DatePickerField
                                label="Visa Expiry Date"
                                name="coapplicantVisaExpiryDate"
                                control={control}
                                error={
                                  errors.coapplicantVisaExpiryDate?.message ??
                                  ''
                                }
                                size="large"
                                placeholder="Select Visa Expiry Date"
                                minDate={dayjs()}
                              />
                            </Col>
                          </>
                        )}
                        <Col xs={24}>
                          <RadioGroupField
                            size="large"
                            name="coapplicantLivingTogether"
                            options={AGREE_OPTIONS}
                            control={control}
                            label="Coapplicant Living Together"
                            error={
                              errors?.coapplicantLivingTogether?.message ?? ''
                            }
                          />
                        </Col>
                        <Col xs={24}>
                          {watchCoapplicantLivingTogether === false && (
                            <Row gutter={[30, 10]}>
                              <Col xs={24}>
                                <SelectField
                                  showSearch={true}
                                  placeholder="Select country"
                                  label="Which country is this person currently in?"
                                  name="coapplicantCountry"
                                  control={control}
                                  options={countryOptions}
                                  error={
                                    errors?.coapplicantCountry?.message ?? ''
                                  }
                                  size="large"
                                />
                              </Col>
                              {watchCoapplicantCountry === 'SG' && (
                                <Col xs={24} md={12}>
                                  <SelectField
                                    placeholder="Permanent Resident?"
                                    label="Are they a Permanent Resident (PR) of this country?"
                                    name="coapplicantPermanentResident"
                                    control={control}
                                    options={AGREE_OPTIONS}
                                    error={
                                      errors?.coapplicantPermanentResident
                                        ?.message ?? ''
                                    }
                                    size="large"
                                  />
                                </Col>
                              )}
                              <Col xs={24}>
                                <RadioGroupField
                                  label="Coapplicant current Residential Status"
                                  name="coapplicantCurrentResidentalStatus"
                                  control={control}
                                  error={
                                    errors.coapplicantCurrentResidentalStatus
                                      ?.message
                                  }
                                  options={RESIDENTIAL_STATUS_OPTIONS}
                                  cols={{ sm: 1, md: 1, lg: 2 }}
                                  size="large"
                                />
                              </Col>
                              <Col xs={24}>
                                <Row gutter={[30, 30]} id="residentalDetails">
                                  {watchCoapplicantCurrentResidentalStatus ===
                                    'renting' && (
                                    <>
                                      <Col xs={24} md={12}>
                                        <InputField
                                          label="Rent Expense (In the currency of your country of residence)"
                                          type="number"
                                          name="coapplicantRentExpense"
                                          control={control}
                                          error={
                                            errors.coapplicantRentExpense
                                              ?.message ?? ''
                                          }
                                          placeholder="Enter the rent expense of your country of residence"
                                          size="large"
                                        />
                                      </Col>
                                      <Col xs={24} md={12}>
                                        <SelectField
                                          label="Frequency of Rent"
                                          options={FREQUENCY_OPTIONS}
                                          name="coapplicantFrequencyOfRent"
                                          filterName="country"
                                          control={control}
                                          size="large"
                                          placeholder="Select your country"
                                          error={
                                            errors.coapplicantFrequencyOfRent
                                              ?.message ?? ''
                                          }
                                        />
                                      </Col>
                                    </>
                                  )}

                                  {watchCoapplicantCurrentResidentalStatus ===
                                    'live_own_place' && (
                                    <Col xs={24}>
                                      <SelectField
                                        options={AGREE_OPTIONS}
                                        label="Is This Place Mortgaged?"
                                        name="coapplicantIsTheResidentialPlaceMortgaged"
                                        control={control}
                                        error={
                                          errors
                                            .coapplicantIsTheResidentialPlaceMortgaged
                                            ?.message ?? ''
                                        }
                                        placeholder="Is this place mortgaged?"
                                        size="large"
                                      />
                                    </Col>
                                  )}

                                  {watchCoapplicantCurrentResidentalStatus ===
                                    'other' && (
                                    <Col xs={24}>
                                      <InputField
                                        label="Provide Further Specification"
                                        name="coapplicantResidentalOtherSpecification"
                                        control={control}
                                        error={
                                          errors
                                            .coapplicantResidentalOtherSpecification
                                            ?.message ?? ''
                                        }
                                        placeholder="Enter further specification"
                                        size="large"
                                      />
                                    </Col>
                                  )}
                                </Row>
                              </Col>
                            </Row>
                          )}
                        </Col>
                        <Col xs={24}>
                          <FormMainLabel
                            index={3}
                            label="Coapplicant Income Details."
                          />
                        </Col>
                        <Col xs={24}>
                          <RadioGroupField
                            size="large"
                            name="coapplicantIncomeSource"
                            options={INCOME_SOURCE_OPTIONS}
                            control={control}
                            label="Coapplicant Income Source"
                            error={
                              errors?.coapplicantIncomeSource?.message ?? ''
                            }
                          />
                        </Col>
                        {watchCoapplicantIncomeSource === 'employee' && (
                          <CoapplicantEmployeeField
                            control={control}
                            errors={errors}
                            watchSalaryCurrency={watchCoapplicantSalaryCurrency}
                            watchAllowanceIncome={
                              watchCoapplicantAllowanceIncome
                            }
                            watchCommissionIncome={
                              watchCoapplicantCommissionIncome
                            }
                            watchAnnualCashBonus={
                              watchCoapplicantAnnualCashBonus
                            }
                            watchStockUnit={watchCoapplicantStockUnit}
                            resetField={resetField}
                          />
                        )}
                        {watchCoapplicantIncomeSource ===
                          'self_employed_trader_or_contractor' && (
                          <CoapplicantContractorField
                            control={control}
                            errors={errors}
                            watchSalaryCurrency={
                              watchCoapplicantCompanySalaryCurrency
                            }
                            watchAllowanceIncome={
                              watchCoapplicantAllowanceIncome
                            }
                            watchCommissionIncome={
                              watchCoapplicantCommissionIncome
                            }
                            watchAnnualCashBonus={
                              watchCoapplicantAnnualCashBonus
                            }
                            watchStockUnit={watchCoapplicantStockUnit}
                          />
                        )}
                        {watchCoapplicantIncomeSource ===
                          'self_employed_company' && (
                          <CoapplicantCompanyField
                            control={control}
                            errors={errors}
                            watchSalaryCurrency={
                              watchCoapplicantCompanySalaryCurrency
                            }
                            watchAllowanceIncome={
                              watchCoapplicantAllowanceIncome
                            }
                            watchCommissionIncome={
                              watchCoapplicantCommissionIncome
                            }
                            watchAnnualCashBonus={
                              watchCoapplicantAnnualCashBonus
                            }
                            watchStockUnit={watchCoapplicantStockUnit}
                          />
                        )}
                        <Col xs={24}>
                          <FormMainLabel
                            index={`${coapplicantNumberIndex + 1}`}
                            label="Does the coapplicant have any non-property loans?"
                          />
                        </Col>
                        <Col xs={24}>
                          <div className="form-control">
                            <RadioGroupField
                              size="large"
                              name="coapplicantNonPropertyLoan"
                              options={NON_PROPERTY_LOAN_OPTIONS}
                              error={
                                errors.coapplicantNonPropertyLoan?.message ?? ''
                              }
                              control={control}
                              cols={{
                                sm: 2,
                                md: 4,
                                lg: 6,
                              }}
                            />
                          </div>
                        </Col>
                        {Array.from(
                          { length: watchCoapplicationNonProperty },
                          (_, index) => {
                            const fieldIndex = index + 1;
                            return (
                              <Col xs={24} key={fieldIndex}>
                                <div className="form-section-header form-control">
                                  <FormMainLabel
                                    index={`L2.${fieldIndex}`}
                                    label={`Non-property Loan ${fieldIndex} Details`}
                                  />
                                  <RadioGroupField
                                    size="large"
                                    name={`coapplicantNonPropertyLoanType${fieldIndex}`}
                                    options={LOAN_TYPE_OPTIONS}
                                    error={
                                      (errors as any)[
                                        `coapplicantNonPropertyLoanType${fieldIndex}`
                                      ]?.message ?? ''
                                    }
                                    control={control}
                                  />
                                </div>
                                <Row gutter={30}>
                                  <Col xs={24} md={12}>
                                    <SelectField
                                      showSearch
                                      options={currencies}
                                      name={`coapplicantNonPropertyLoanCurrency${fieldIndex}`}
                                      control={control}
                                      size="large"
                                      label="Currency"
                                      placeholder="Select currency type"
                                      error={
                                        (errors as any)[
                                          `coapplicantNonPropertyLoanCurrency${fieldIndex}`
                                        ]?.message ?? ''
                                      }
                                    />
                                  </Col>
                                  <Col xs={24} md={12}>
                                    <InputField
                                      label="Loan Balance"
                                      name={`coapplicantNonPropertyLoanBalance${fieldIndex}`}
                                      control={control}
                                      error={
                                        (errors as any)[
                                          `coapplicantNonPropertyLoanBalance${fieldIndex}`
                                        ]?.message ?? ''
                                      }
                                      placeholder="Enter your loan balance"
                                      size="large"
                                      type="number"
                                    />
                                  </Col>
                                  <Col xs={24} md={12}>
                                    <InputField
                                      label="Repayment loan term"
                                      name={`coapplicantNonPropertyLoanRepaymentTerm${fieldIndex}`}
                                      control={control}
                                      error={
                                        (errors as any)[
                                          `coapplicantNonPropertyLoanRepaymentTerm${fieldIndex}`
                                        ]?.message ?? ''
                                      }
                                      placeholder="Years left on loan term"
                                      size="large"
                                      type="number"
                                      suffix="Years"
                                    />
                                  </Col>
                                  <Col xs={24} md={12}>
                                    <InputField
                                      label="Repayment"
                                      name={`coapplicantNonPropertyLoanRepaymentSum${fieldIndex}`}
                                      control={control}
                                      error={
                                        (errors as any)[
                                          `coapplicantNonPropertyLoanRepaymentSum${fieldIndex}`
                                        ]?.message ?? ''
                                      }
                                      placeholder="Your repayment Sum"
                                      size="large"
                                      type="number"
                                    />
                                  </Col>
                                  <Col xs={24}>
                                    <RadioGroupField
                                      size="large"
                                      name={`coapplicantNonPropertyLoanRepaymentFrequency${fieldIndex}`}
                                      options={FREQUENCY_OPTIONS}
                                      label="Repayment Frequency"
                                      error={
                                        (errors as any)[
                                          `coapplicantNonPropertyLoanRepaymentFrequency${fieldIndex}`
                                        ]?.message ?? ''
                                      }
                                      control={control}
                                    />
                                  </Col>
                                </Row>
                              </Col>
                            );
                          },
                        )}
                        <Col xs={24}>
                          <FormMainLabel
                            index={`${coapplicantNumberIndex + 2}`}
                            label="What's the coapplicant's total credit card limit?"
                          />
                        </Col>
                        <Col xs={24} md={12}>
                          <SelectField
                            options={currencies}
                            name="coapplicantCreditCardCurrency"
                            control={control}
                            size="large"
                            showSearch={true}
                            label="Credit Card Currency"
                            placeholder="Select currency type"
                            error={
                              errors?.coapplicantCreditCardCurrency?.message ??
                              ''
                            }
                          />
                        </Col>
                        <Col xs={24} md={12}>
                          <InputField
                            label="Credit Card Limit Amount"
                            name="coapplicantCreditCardLimit"
                            control={control}
                            error={
                              errors?.coapplicantCreditCardLimit?.message ?? ''
                            }
                            placeholder="Enter your credit card limit"
                            size="large"
                            type="number"
                            prefix={watchCoapplicantCreditCardCurrency ?? ''}
                          />
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Card>

                <Card
                  title={
                    <Typography.Title level={4} style={{ margin: 0 }}>
                      Other Details
                    </Typography.Title>
                  }
                >
                  <Col xs={24}>
                    <div className="form-control">
                      <FormMainLabel
                        index={1}
                        label={'Help us understand your situation better.'}
                      />
                      <SelectField
                        name="taxBenefitAwareness"
                        control={control}
                        options={ADDITIONAL_TAX_BENEFITS}
                        label="Are you aware of the tax benefits available to overseas residents with Australian property investments?"
                        error={errors?.taxBenefitAwareness?.message ?? ''}
                        size="large"
                      />
                      <SelectField
                        name="whereDidYouHearAboutUs"
                        showSearch
                        control={control}
                        options={WHERE_DID_YOU_HEAR_ABOUT_US_OPTIONS}
                        label="Where did you hear about us?"
                        error={errors?.whereDidYouHearAboutUs?.message ?? ''}
                        size="large"
                      />
                    </div>
                  </Col>
                  <Col xs={24}>
                    <div className="form-section-header form-control">
                      <FormMainLabel
                        index={2}
                        label={incomeDetailLabelMapper['userMessage']}
                      />
                      <TextAreaField
                        name="userMessage"
                        control={control}
                        error={errors?.userMessage?.message ?? ''}
                        placeholder="Please name 2-3 suburbs you would like to receive suburb reports for. If there is anything else you would like to know, please highlight it here."
                        size="large"
                        rows={4}
                      />
                    </div>
                    <div ref={termsRef} className="terms-and-conditions">
                      <MessageBox style={{ marginTop: '3rem' }}>
                        <Typography.Title level={5} style={{ marginTop: '0' }}>
                          Note: Please ensure you have answered all questions
                          before submitting. The more accurate your answers, the
                          more accurate your assessment.
                        </Typography.Title>
                        <Typography.Paragraph style={{ marginBottom: '0' }}>
                          We are collecting your personal information to assist
                          you with your home loan application or any other
                          ancillary products. By clicking the submit button you
                          have read, consented, and agree to be bound by our{' '}
                          <a
                            href="https://www.odinmortgage.com/privacy-security/"
                            target="_blank"
                            style={{
                              fontWeight: 'bold',
                              textDecoration: 'underline',
                            }}
                          >
                            Privacy Policy
                          </a>{' '}
                          &{' '}
                          <a
                            href="https://www.odinmortgage.com/credit-guide/"
                            target="_blank"
                            style={{
                              fontWeight: 'bold',
                              textDecoration: 'underline',
                            }}
                          >
                            Credit Guide.
                          </a>
                        </Typography.Paragraph>

                        <Checkbox
                          style={{
                            marginTop: '1.5rem',
                            fontSize: '15px',
                            fontWeight: 500,
                            padding: '10px 14px',
                            border: acceptedTerms
                              ? '1.5px solid #52c41a'
                              : '1.5px solid #d9d9d9',
                            borderRadius: '8px',
                            background: acceptedTerms ? '#f6ffed' : '#ffffff',
                            width: '100%',
                          }}
                          checked={acceptedTerms}
                          onChange={(e) => setAcceptedTerms(e.target.checked)}
                        >
                          I accept the terms and conditions.
                        </Checkbox>
                      </MessageBox>
                    </div>
                  </Col>
                </Card>

                <Col xs={24}>
                  <Button
                    ref={submitButtonRef}
                    type="primary"
                    htmlType="submit"
                    style={{ display: 'none' }}
                  >
                    Submit
                  </Button>
                </Col>
              </Row>
            </form>
          </Col>
        </Row>
        <Drawer
          title="Review Submission"
          open={isDrawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={1000}
          footer={
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
              }}
            >
              <Button onClick={() => setDrawerOpen(false)} size="large">
                Cancel
              </Button>
              <Button
                type="primary"
                size="large"
                loading={postRefinanceRequestLoading}
                disabled={postRefinanceRequestLoading}
                onClick={handleSubmit((formData) => {
                  const payload: IncomeDetailPayload = {
                    data: formData,
                    step: 'income_detail',
                    ...(id ? { application_id: id } : {}),
                  };

                  postRefinanceRequest(payload)
                    .unwrap()
                    .then((res) => {
                      displaySuccess(res?.message);
                      window.parent.postMessage({ refinance_id: id }, '*');
                      setModalOpen(true);
                      setDrawerOpen(false);
                    })
                    .catch((err) => {
                      displayError(err);
                    });
                })}
              >
                Confirm & Submit
              </Button>
            </div>
          }
        >
          <ReviewData
            stepData={[
              {
                step: 'primary_detail',
                data: primaryDetailData,
              },
              {
                step: 'income_detail',
                data: getValues(),
              },
            ]}
            id={id ?? ''}
            type="refinance"
          />
        </Drawer>
        <MeetingModal
          isOpen={modalOpen}
          onClose={handleModalClose}
          onOk={handleModalOk}
        />
      </Container>
    </StepperFormLayout>
  );
};

export default IncomeDetail;
